import { HttpErrorResponse } from '@angular/common/http'
import { Component, Input, OnDestroy, OnInit } from '@angular/core'
import { FormControl } from '@angular/forms'
import { Device } from '@dms/interfaces/dms.interfaces'
import { DeviceStore } from '@dms/stores/device.store'
import { ApplicationLifecycleService } from '@services/application-lifecycle.service'
import { BillingService } from '@services/billing/billing.service'
import { TsdSubscription } from '@services/proficloud.interfaces'
import { UiService } from '@services/ui/ui.service'
import { SelectableInterface } from '@shared/components/pc-input-select-multiple-table/pc-input-select-multiple-table.component'
import { PcStatusOverlayService } from '@shared/services/pc-status-overlay/pc-status-overlay.service'
import { sharedAnimations } from '@shared/shared.animations'
import { ProficloudInputConfig } from '@shared/type-definitions/types'
import { BehaviorSubject, combineLatest, forkJoin, Subscription } from 'rxjs'
import { BillingStore } from '../../../stores/billing.store'

@Component({
  selector: 'app-subscription-metrics-modal',
  animations: [sharedAnimations.heightSlide],
  templateUrl: './subscription-metrics-modal.component.html',
  styleUrls: ['./subscription-metrics-modal.component.scss'],
})
export class SubscriptionMetricsModalComponent implements OnInit, OnDestroy {
  @Input({ required: true }) singleDevice: Device | true // when true, we show everything

  availableEndpointMetrics$: BehaviorSubject<Record<string, string[]> | null> = new BehaviorSubject(null)

  tempAssignedMetrics$: BehaviorSubject<number> = new BehaviorSubject(0)

  deviceSubscription?: Subscription
  devicesMetricsSubscription?: Subscription

  selectableMetrics: {
    device: Device
    selectableMetrics: SelectableInterface<string>[]
    config: ProficloudInputConfig<string[]>
    show: boolean
  }[] = []

  constructor(
    public billing: BillingService,
    private billingStore: BillingStore,
    private statusOverlay: PcStatusOverlayService,
    private deviceStore: DeviceStore,
    public ui: UiService,
    private applicationLifecycle: ApplicationLifecycleService
  ) {}

  public ngOnInit(): void {
    this.loadAvailableMetrics()
    this.setupForm()
  }

  public ngOnDestroy(): void {
    for (let subscription of [this.deviceSubscription, this.devicesMetricsSubscription]) {
      if (subscription !== undefined) {
        subscription.unsubscribe()
      }
    }
  }

  public countTempAssignedMetrics(): void {
    this.tempAssignedMetrics$.next(this.selectableMetrics.map((device) => device.config.control.value.length).reduce((a, b) => a + b))
  }

  // TODO: will be sourced out into a tsd billing store thingy.
  private setupForm(): void {
    this.devicesMetricsSubscription = combineLatest([this.deviceStore.devices$, this.billingStore.allSubscriptions$, this.availableEndpointMetrics$]).subscribe(
      ([devices, allSubscriptions, availableMetrics]) => {
        devices = devices || []
        let tsdSubscriptions = allSubscriptions.tsdSubscriptions
        if (tsdSubscriptions === undefined || tsdSubscriptions.length === 0 || availableMetrics === null) {
          return
        }
        let tsdSubscription = tsdSubscriptions[0]

        let assignedMetrics = this.getAssignedMetrics(devices, tsdSubscription)

        this.selectableMetrics = devices.map((device) => {
          let deviceAvailableMetrics = availableMetrics[device.endpointId]
          let deviceAssignedMetrics = assignedMetrics[device.endpointId]
          return {
            device: device,
            selectableMetrics: deviceAvailableMetrics.map((availableMetric) => {
              return {
                selected: deviceAssignedMetrics.includes(availableMetric),
                value: availableMetric,
                displayValue: availableMetric,
              }
            }),
            config: this.getDeviceInputConfig(device, deviceAssignedMetrics),
            show: this.singleDevice === true ? true : device.metadata.uuid === this.singleDevice.metadata.uuid,
          }
        })
        this.countTempAssignedMetrics()
      }
    )
  }

  private getDeviceInputConfig(device: Device, deviceAssignedMetrics: string[]): ProficloudInputConfig<string[]> {
    return {
      control: new FormControl(deviceAssignedMetrics, { nonNullable: true }),
      type: 'select',
      key: `metrics-${device.metadata.uuid}`,
      analyticsTag: `select-metrics-device-${device.metadata.uuid}`,
      inputId: `select-metrics-device-${device.metadata.uuid}`,
    }
  }

  private getAssignedMetrics(devices: Device[], tsdSubscription: TsdSubscription) {
    let assignedMetrics: Record<string, string[]> = {}
    devices.forEach((device) => {
      assignedMetrics[device.endpointId] = []
    })

    tsdSubscription.metricsUsed.forEach((listEntry) => {
      Object.entries(listEntry).forEach(([endpointId, usedMetrics]) => {
        assignedMetrics[endpointId] = usedMetrics
      })
    })
    return assignedMetrics
  }

  private loadAvailableMetrics(): void {
    this.deviceSubscription = this.deviceStore.devices$.subscribe((devices) => {
      if (devices === undefined) {
        return
      }

      let tempAvailableMetrics: Record<string, string[]> = {}
      devices.forEach((device) => (tempAvailableMetrics[device.endpointId] = []))

      let allMetricsObservables = devices.map((device) => this.billingStore.listAvailableEndpointMetrics(device))

      forkJoin(allMetricsObservables).subscribe((availableMetrics) => {
        availableMetrics.forEach((availableMetrics) => {
          Object.entries(availableMetrics).forEach(([endpointId, deviceAvailableMetrics]) => {
            tempAvailableMetrics[endpointId] = deviceAvailableMetrics
          })
        })
        this.availableEndpointMetrics$.next(tempAvailableMetrics)
      })
    })
  }

  public queryTsdDevices(query: string): void {
    this.selectableMetrics.forEach(
      (metric) =>
        (metric.show =
          metric.device.metadata.deviceName.toLocaleLowerCase().includes(query.toLowerCase()) ||
          metric.device.metadata.uuid.toLowerCase().includes(query.toLowerCase()) ||
          metric.selectableMetrics.some((metric) => metric.value.toLowerCase().includes(query.toLowerCase())))
    )
  }

  public assignMetricsClick(): void {
    let payload: { metrics: Record<string, string[]>[] } = { metrics: [] }
    this.selectableMetrics.forEach((device) => {
      payload.metrics.push({ [device.device.endpointId]: device.config.control.value })
    })

    this.bulkUpdateMetrics(payload)
  }

  private bulkUpdateMetrics(payload: { metrics: Record<string, string[]>[] }) {
    this.billing.showAssignMetricsModal$.next(false)
    this.statusOverlay.queueLoadingStatus$.next({ key: 'assignTsdMetrics', config: this.ui.statusOverlays.updateMetricsBusy })

    this.billingStore.bulkAssignMetrics(this.billing.tsdSubscription.id, payload).subscribe({
      next: () => {
        this.statusOverlay.resolveLoadingStatus$.next({ key: 'assignTsdMetrics', config: this.ui.statusOverlays.updateMetricsSuccess })
      },
      error: (error: HttpErrorResponse) => {
        this.applicationLifecycle.logoutOnUnauthorised(error)
        this.statusOverlay.resolveLoadingStatus$.next({ key: 'assignTsdMetrics', config: this.ui.statusOverlays.updateMetricsError })
      },
    })
  }
}
